import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BuildingOfficeIcon, ArchiveBoxIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Share2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientsAPI, postsAPI } from '../../services/api';
import { Card } from '../ui';

const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const { isRTL } = useLanguage();
  const [archivedClientsCount, setArchivedClientsCount] = useState(0);
  const [deletedPostsCount, setDeletedPostsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      fetchCounts();
    }
  }, [isAdmin]);

  const fetchCounts = async () => {
    try {
      const [archivedClientsResponse, deletedPostsResponse] = await Promise.all([
        clientsAPI.getArchived().catch(() => ({ data: { clients: [] } })),
        postsAPI.getDeleted().catch(() => ({ data: { posts: [] } })),
      ]);
      
      setArchivedClientsCount(archivedClientsResponse.data.clients?.length || 0);
      setDeletedPostsCount(deletedPostsResponse.data.posts?.length || 0);
    } catch (err) {
      // Ignore errors, just show 0
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-gray-600">
          {isRTL ? 'غير مصرح لك بالوصول إلى هذه الصفحة' : 'You do not have access to this page'}
        </p>
      </div>
    );
  }

  const settingsCards = [
    {
      id: 'agency',
      title: isRTL ? 'معلومات الوكالة' : 'Agency Information',
      description: isRTL 
        ? 'تحديث اسم الوكالة والشعار'
        : 'Update agency name and logo',
      icon: BuildingOfficeIcon,
      href: '/settings/agency',
      color: 'blue',
    },
    {
      id: 'social-media',
      title: isRTL ? 'منصات التواصل' : 'Social Media',
      description: isRTL 
        ? 'ربط حساباتك على منصات التواصل الاجتماعي'
        : 'Connect your social media accounts',
      icon: Share2,
      href: '/settings/social-media',
      color: 'purple',
    },
    {
      id: 'trash',
      title: isRTL ? 'سلة المحذوفات' : 'Trash Bin',
      description: isRTL 
        ? `البوستات المحذوفة (${deletedPostsCount})`
        : `Deleted posts (${deletedPostsCount})`,
      icon: TrashIcon,
      href: '/posts/trash',
      color: 'red',
    },
    {
      id: 'archived-clients',
      title: isRTL ? 'العملاء المؤرشفين' : 'Archived Clients',
      description: isRTL 
        ? `العملاء المؤرشفين (${archivedClientsCount})`
        : `Archived clients (${archivedClientsCount})`,
      icon: ArchiveBoxIcon,
      href: '/settings/archived-clients',
      color: 'yellow',
    },
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    red: 'bg-red-100 text-red-600',
    yellow: 'bg-yellow-100 text-yellow-600',
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">
            {isRTL ? 'إدارة النظام' : 'System Management'}
          </p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1">
            {isRTL ? 'الإعدادات' : 'Settings'}
          </h1>
          <p className="text-slate-600 text-sm sm:text-base mt-1">
            {isRTL
              ? 'إدارة إعدادات الوكالة، الباقات، وسلة المحذوفات من مكان واحد.'
              : 'Manage agency settings, plans, and trash from one place.'}
          </p>
        </div>
      </Card>

      {/* Settings Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link key={card.id} to={card.href} className="block">
              <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer h-full rounded-2xl border border-slate-200 bg-white">
                <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-4`}>
                  <div className={`flex items-center justify-center h-12 w-12 rounded-xl ${colorClasses[card.color]}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-1">
                      {card.title}
                    </h2>
                    <p className="text-sm text-slate-600">
                      {card.description}
                    </p>
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SettingsPage;
